/**
 * Custom hook that owns the per-authorization form state for the
 * Auth Letter Upload modal.
 *
 * Each entry in `formStates` is one authorization tab in the sidebar and is
 * the single source of truth for that tab. The hook exposes helpers for
 * mutating, adding, and removing entries so the parent modal stays slim.
 *
 * The list always holds at least one entry: an empty list would make
 * `handleFormFieldChange` a no-op and leave the form unresponsive.
 */
import { useCallback, useState } from "react";
import { AuthLetterUploadFormState } from "../../../../data-model/candidAuthorizationModel";

/** Blank form state used for newly added authorization entries. */
export const EMPTY_FORM_STATE: AuthLetterUploadFormState = {
  authorizationNumber: "",
  payerName: "",
  startDate: "",
  endDate: "",
  serviceCodes: [],
  quantity: "",
  units: "",
  notes: "",
  dx_codes: [],
  npiBillingNumber: "",
  payerId: "",
};

/**
 * Builds a blank form state for a newly added authorization tab.
 *
 * @returns A fresh AuthLetterUploadFormState
 */
const createEmptyFormState = (): AuthLetterUploadFormState => ({
  ...EMPTY_FORM_STATE,
  authorizationNumber: "New Authorization",
});

/**
 * Manages the array of form states for every authorization tab, seeded with a
 * single blank tab.
 *
 * @param selectedIndex - Index of the currently active authorization tab.
 * @returns Form states, the currently selected form, and mutation helpers.
 */
export const useAuthFormStates = (selectedIndex: number) => {
  const [formStates, setFormStates] = useState<AuthLetterUploadFormState[]>(
    () => [createEmptyFormState()],
  );

  /** Appends a blank form-state entry (used by the "Add More" button). */
  const addEmptyFormState = useCallback(() => {
    setFormStates((prev) => [...prev, createEmptyFormState()]);
  }, []);

  /** Removes the entry at the given index, falling back to one blank tab. */
  const removeFormState = useCallback((index: number) => {
    setFormStates((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [createEmptyFormState()];
    });
  }, []);

  /** Updates a single field on the currently-selected form state. */
  const handleFormFieldChange = useCallback(
    <K extends keyof AuthLetterUploadFormState>(
      field: K,
      fieldValue: AuthLetterUploadFormState[K],
    ) => {
      setFormStates((prev) =>
        prev.map((s, i) =>
          i === selectedIndex ? { ...s, [field]: fieldValue } : s,
        ),
      );
    },
    [selectedIndex],
  );

  const currentFormState = formStates[selectedIndex] ?? EMPTY_FORM_STATE;

  return {
    formStates,
    currentFormState,
    handleFormFieldChange,
    addEmptyFormState,
    removeFormState,
  };
};
